import {useEffect, useState} from 'react';
import ClickAwayListener from 'react-click-away-listener';
import {faCalendar, faLocationDot, faUsers} from '@fortawesome/pro-light-svg-icons';
import {parseISO} from 'date-fns';
import {format} from 'date-fns/format';
import {Formik} from 'formik';
import {useRouter} from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import {useConsumerCart, useDerivedMenuData} from '@/components/features/menu/hooks';
import useEventBarCoachmarkVisible from '@/components/global/layout/AppBar/hooks/useEventBarCoachmarkVisible';
import {EventBarTrackingEvent} from '@/components/global/layout/EventBar/types';
import useOrderAccounts, {
  type OrderAccount,
} from '@/components/global/layout/EventBar/useOrderAccounts';
import {useFulfillmentDetailState} from '@/components/global/providers/FulfillmentDetailStateProvider';
import {useGlobalFulfillmentDetailMutation} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {useSelectedCorpAccountUuidContext} from '@/components/global/providers/SelectedCorpAccountUuidProvider';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {
  FulfillmentDetailAddressInput,
  FulfillmentDetailStrategy,
  FulfillmentDetailUpdateInput,
} from '@/graphql/types';
import useAppBarLoading from '@/hooks/useAppBarLoading';
import {useGoogleMapsGeocode} from '@/hooks/useGoogleMapsGeocode';
import useIdentity from '@/hooks/useIdentity';
import useSavedAddressSearch from '@/hooks/useSavedAddressSearch';
import useTracking from '@/hooks/useTracking';
import {SEARCH_NEW_PATH, SEARCH_ORDER_ID_PATH} from '@/paths';
import {
  compilePath,
  getAddressInput,
  gtm,
  MicroConversionEnum,
  trackMicroConversion,
} from '@/utils';
import {formatEventBarDate} from '@/utils/dateFormat';
import {fulfillmentDetailAddressInputToString} from '@/utils/geocodedAddressResult';
import {deliveryAddressEnteredPayload} from '@/utils/googleAnalyticsEvents';
import {
  type GeocodedAddress,
  type GeocodedAddressResult,
  type SavedAddress,
} from '@/utils/googleAutocomplete';
import {stripQueryParams, updateRouteQueryParams} from '@/utils/routing';
import Coachmark from './Coachmark';
import ApplyDetailsConfirmationModal from './ConfirmationModal';
import DateTime from './DateTime';
import Details from './Details';
import EventBarPopup from './EventBarPopup';
import Location from './Location';
import {type DropdownDismissFunction} from './types';

export type FulfillmentDetailFormProps = {
  addressString: string;
  attendees: number | null;
  eventDate: string | null;
  eventName: string | null;
  eventOrderType: FulfillmentDetailStrategy;
  eventTime: string | null;
  geocodedAddressResult: GeocodedAddressResult | null;
  googlePlace: google.maps.places.AutocompletePrediction | null;
  orderAccount?: OrderAccount | null;
  savedAddress: SavedAddress | null;
};

export type ValidateAddressFunction = (values: FulfillmentDetailFormProps) => Promise<{
  addressIsValid: boolean;
  addressUpdateInput: FulfillmentDetailAddressInput;
}>;

const EventBar: React.FC<{onUpdateApplied?: () => void}> = ({onUpdateApplied}) => {
  const {t} = useTranslation('app-bar');
  const {isOnMenuPage, isOnSearchPage} = useStoreNextRouter();
  const {
    cachedMultipleAddresses,
    fulfillmentDetailClickHandler,
    fulfillmentDetail,
    fulfillmentDetailState,
    FulfillmentDetailStates,
    fallbackAddress,
    setCachedMultipleAddresses,
    setCoachmarkDismissed,
  } = useFulfillmentDetailState();
  const {updateFulfillmentDetail} = useGlobalFulfillmentDetailMutation();
  const {
    address,
    eventAt,
    eventLocalTime,
    eventName,
    eventOn,
    headcount,
    id: fulfillmentDetailId,
    strategy,
  } = fulfillmentDetail || {};
  const isLocationOpen = fulfillmentDetailState === FulfillmentDetailStates.LOCATION;
  const isDateTimeOpen = fulfillmentDetailState === FulfillmentDetailStates.DATETIME;
  const isDetailsOpen = fulfillmentDetailState === FulfillmentDetailStates.DETAILS;
  const isCoachmarkOpen = fulfillmentDetailState === FulfillmentDetailStates.COACHMARK;
  const isEventBarCoachmarkVisible = useEventBarCoachmarkVisible();
  const isApplyDetailsConfirmationOpen =
    fulfillmentDetailState === FulfillmentDetailStates.APPLY_DETAILS_CONFIRMATION;
  const {caterer} = useDerivedMenuData();
  const {data: identityData} = useIdentity();
  const {shouldRenderSelectOrderAccount, availableCorpAccountOptions} = useOrderAccounts();
  const tracker = useTracking();
  const consumerCart = useConsumerCart();
  const corpAccountId = identityData?.me?.consumerAccount?.corporateAccount?.id ?? null;
  const fallbackAddressString = fallbackAddress
    ? fulfillmentDetailAddressInputToString(fallbackAddress)
    : '';
  const {id: catererId} = caterer ?? {};
  const cartId = consumerCart?.id ?? null;
  const router = useRouter();
  const {fetchGeocodedAddress} = useGoogleMapsGeocode();
  const [showIncompleteAddressWarning, setShowIncompleteAddressWarning] = useState(false);
  const [multipleAddresses, setMultipleAddresses] = useState<GeocodedAddress[] | null>(null);
  const [isLocationSelectOpen, setIsLocationSelectOpen] = useState(false);
  const corpAccount =
    fulfillmentDetail?.corpAccount || identityData?.me?.consumerAccount?.corporateAccount;
  const {setShowAppBarLoading} = useAppBarLoading();
  const {accountUuid, setAccountUuid} = useSelectedCorpAccountUuidContext();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (cachedMultipleAddresses.length > 1) setMultipleAddresses(cachedMultipleAddresses);
  }, [cachedMultipleAddresses]);

  const trackEvent = (
    trackingEventName: EventBarTrackingEvent,
    misc: Record<string, unknown> = {},
    overrides: Record<string, unknown> = {},
  ) => {
    tracker.track(trackingEventName, {
      page: window.location.pathname,
      sub_category: 'navigation bar',
      caterer_id: catererId || null,
      misc_json: JSON.stringify({
        consumer_cart_id: cartId,
        fulfillment_detail_id: fulfillmentDetailId,
        street: address?.street,
        city: address?.city,
        state: address?.state,
        corpAccountId: corpAccountId || null,
        eventName,
        orderType: strategy,
        eventDate: eventOn,
        eventTime: eventLocalTime,
        headcountNumber: headcount,
        ...misc,
      }),
      ...overrides,
    });
  };

  const handleDismissCoachmark = () => {
    if (isCoachmarkOpen) {
      setCoachmarkDismissed();
      tracker.trackClick('event-bar-coachmark-dismissed-by-fulfillment-detail-click');
    }
  };

  const dismissActiveDropdown = async ({
    trackingMisc = {},
    event = null,
    dirty = false,
  }: {
    trackingMisc?: Record<string, unknown>;
    event?: Event | null;
    dirty?: boolean;
  }) => {
    if (isLocationOpen) trackEvent('location-details-dismissed', trackingMisc);
    else if (isDateTimeOpen) trackEvent('date-time-dismissed', trackingMisc);
    else if (isDetailsOpen) trackEvent('event-details-dismissed', trackingMisc);
    else if (isApplyDetailsConfirmationOpen)
      trackEvent('apply-details-confirmation-dismissed', trackingMisc);

    // if a user clicks a button in the cart marked with data-eventbar-ignore, we don't want to
    // dismiss all dropdowns (e.g. the button that opens the details dropdown with the guest count)
    if ((event?.target as HTMLElement)?.closest('[data-eventbar-ignore]')) return;

    if (dirty) {
      fulfillmentDetailClickHandler(FulfillmentDetailStates.APPLY_DETAILS_CONFIRMATION);
    } else if (!isCoachmarkOpen) {
      fulfillmentDetailClickHandler(FulfillmentDetailStates.NONE);
    }
  };

  const onDropdownDismiss: DropdownDismissFunction = async (formStatus, trackingMisc = {}) => {
    dismissActiveDropdown({trackingMisc});

    if (formStatus.updated) onUpdateApplied?.();

    if (isOnSearchPage) {
      if (formStatus.updated)
        updateRouteQueryParams(router, stripQueryParams(router, ['page', 'addressString']));
    } else if (!isOnMenuPage) {
      if (formStatus.fulfillmentDetailId || fulfillmentDetailId)
        router.push(
          compilePath(SEARCH_ORDER_ID_PATH, {
            orderId: formStatus.fulfillmentDetailId || fulfillmentDetailId,
          }),
        );
      else router.push(compilePath(SEARCH_NEW_PATH));
    } else {
      setShowAppBarLoading(false);
    }
  };

  const validateAddress: ValidateAddressFunction = async (values: FulfillmentDetailFormProps) => {
    let addressIsValid = false;
    let addressUpdateInput = {};
    setShowIncompleteAddressWarning(false);

    // If the address is empty, show a warning
    if (values?.addressString === '') {
      setShowIncompleteAddressWarning(true);
      return {addressIsValid, addressUpdateInput};
    }

    // Get address input
    // Skip string comparison if using a saved address
    const hasChangedAddress =
      values?.savedAddress || values?.addressString !== address?.fullAddress;
    const addressInput = hasChangedAddress
      ? await getAddressInput({fetchGeocodedAddress, values})
      : undefined;

    // Set multiple addresses and show incomplete address warning if needed
    const [multipleAddresses, updateInputAddress] = Array.isArray(addressInput)
      ? [addressInput, null]
      : [null, addressInput];
    let showIncompleteAddressWarning = false;
    if (multipleAddresses) setMultipleAddresses(multipleAddresses);
    else if (hasChangedAddress && updateInputAddress == null) {
      setShowIncompleteAddressWarning(true);
      showIncompleteAddressWarning = true;
    }

    // Get the input to be updated - do not update if values have not changed
    addressUpdateInput = Object.fromEntries(
      Object.entries({
        ...updateInputAddress,
        strategy: values?.eventOrderType !== strategy ? values.eventOrderType : undefined,
        corpAccountId:
          values?.orderAccount?.id !== corpAccount?.id ? values.orderAccount?.id : undefined,
      }).filter(([, value]) => value !== undefined),
    );

    addressIsValid =
      (!multipleAddresses || multipleAddresses.length === 0) && !showIncompleteAddressWarning;

    return {addressIsValid, addressUpdateInput};
  };

  const clearAddressWarnings = () => {
    setShowIncompleteAddressWarning(false);
    setMultipleAddresses(null);
  };

  const initialOrderAccount = shouldRenderSelectOrderAccount
    ? availableCorpAccountOptions.find(({uuid, id}) =>
        accountUuid != null ? uuid === accountUuid : id === corpAccount?.id,
      )
    : undefined;

  const {addresses: savedAddresses} = useSavedAddressSearch({
    searchTerm: ' ',
    limit: 2,
    corpAccountId: initialOrderAccount?.id,
  });

  const locationCta = isOnMenuPage
    ? t('eventBar.eventLocation.cta.menu')
    : isOnSearchPage
      ? t('eventBar.eventLocation.cta.search')
      : t('eventBar.eventLocation.cta.accountPage');

  const initialValues = {
    addressString: address?.fullAddress || fallbackAddressString || '',
    attendees: headcount || null,
    eventDate: eventOn || null,
    eventName: eventName ?? '',
    eventOrderType: strategy || FulfillmentDetailStrategy.Delivery,
    eventTime: eventLocalTime || null,
    geocodedAddressResult: null,
    googlePlace: null,
    ...(initialOrderAccount ? {orderAccount: initialOrderAccount} : undefined),
    savedAddress: null,
  } satisfies FulfillmentDetailFormProps;

  const dirty = (values: FulfillmentDetailFormProps) => {
    // Check if the values are dirty by comparing them to the initial values
    const keys = Object.keys(initialValues);
    const dirtyValues = keys.filter(key => {
      const initialValue = initialValues[key as keyof FulfillmentDetailFormProps];

      // Handle address string specifically to remove USA suffix
      // This is a workaround for the issue where geocoded addresses include ", USA" at the end
      // and we want to ignore that when checking for dirty values
      values.addressString = values.addressString?.replace(/, USA$/, '');
      const value = values[key as keyof FulfillmentDetailFormProps];

      // Handle orderAccount objects
      if (key === 'orderAccount') {
        return (
          (initialValue as FulfillmentDetailFormProps['orderAccount'])?.id !==
          (value as FulfillmentDetailFormProps['orderAccount'])?.id
        );
      }

      // edge case where someone clears out address input and clicks outside of dropdown
      if (key === 'addressString' && value === '') {
        return false;
      }

      // We want to ignore the savedAddress field when checking for dirty values
      // since we are comparing it against addressString, not the saved address
      return initialValue !== value && key !== 'savedAddress';
    });
    return dirtyValues.length > 0;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values, formikHelpers) => {
        // This handles submitting all fulfillment details with one action.

        // get address update input and check if address is valid
        const {addressIsValid, addressUpdateInput} = await validateAddress(values);

        if (!addressIsValid) {
          fulfillmentDetailClickHandler(FulfillmentDetailStates.LOCATION);
          return;
        }

        if (values?.orderAccount?.uuid && values.orderAccount.uuid !== accountUuid) {
          setAccountUuid(values.orderAccount.uuid);
        }

        // build up input to send to mutation
        const fulfillmentDetailInput: FulfillmentDetailUpdateInput = {
          ...addressUpdateInput,
          ...(values?.eventTime !== initialValues.eventTime
            ? {eventLocalTime: values.eventTime}
            : {}),
          ...(values?.eventDate !== initialValues.eventDate ? {eventOn: values.eventDate} : {}),
          ...(values?.eventName !== initialValues.eventName ? {eventName: values.eventName} : {}),
          ...(values?.attendees !== initialValues.attendees ? {headcount: values.attendees} : {}),
        };

        const matchedSavedAddress =
          savedAddresses && fulfillmentDetailInput?.addressId
            ? savedAddresses.find(add => add.placeId === fulfillmentDetailInput.addressId)
            : undefined;

        // build up payload for tracking events
        const trackingParams = {
          street:
            matchedSavedAddress?.street ||
            fulfillmentDetailInput?.address?.street1 ||
            address?.street,
          city: matchedSavedAddress?.city || fulfillmentDetailInput?.address?.city || address?.city,
          state:
            matchedSavedAddress?.state || fulfillmentDetailInput?.address?.state || address?.state,
          orderType: fulfillmentDetailInput?.strategy || strategy,
          eventDate: values?.eventDate,
          eventTime: values?.eventTime,
          eventName: values?.eventName,
          headcountNumber: values?.attendees,
        };

        // handle tracking
        if (isLocationOpen) {
          gtm.dataLayer(deliveryAddressEnteredPayload({clickText: locationCta}));
          trackEvent('location-details-search-clicked');
        }

        if (isDateTimeOpen) {
          trackEvent('date-time-search-clicked', trackingParams);
        }

        if (isDetailsOpen) {
          trackEvent('details-search-clicked', trackingParams);
        }

        const hasChanges = Object.keys(fulfillmentDetailInput).length > 0;

        // No changes and submit is clicked
        if (isLocationOpen && !hasChanges && addressIsValid) {
          onDropdownDismiss({updated: false});
          formikHelpers.resetForm();
          return;
        } else if (!hasChanges) {
          onDropdownDismiss({updated: false}, trackingParams);
          formikHelpers.resetForm();
          return;
        }

        // submission
        let data: Awaited<ReturnType<typeof updateFulfillmentDetail>> | undefined = undefined;
        if (Object.keys(fulfillmentDetailInput).length > 0) {
          try {
            setShowAppBarLoading(true);
            data = await updateFulfillmentDetail(fulfillmentDetailInput);

            // Reset cached multiple addresses on successful update
            setCachedMultipleAddresses([]);
          } catch (error) {
            setShowAppBarLoading(false);
            // Location component doesn't seem to have any place to render errors
            formikHelpers.setStatus({error});
            trackEvent('event-bar-fulfillment-detail-error', fulfillmentDetailInput);
            fulfillmentDetailClickHandler(FulfillmentDetailStates.LOCATION);
            return;
          }

          const addressTrackingParams = {
            ...(fulfillmentDetailInput.address ? {address: fulfillmentDetailInput.address} : {}),
            ...(fulfillmentDetailInput.addressId
              ? {address: fulfillmentDetailInput.addressId}
              : {}),
          };

          if (Object.keys(addressTrackingParams).length > 0)
            trackEvent('address-updated', addressTrackingParams);
          if (fulfillmentDetailInput?.strategy) {
            trackEvent('order-type-updated', {orderType: fulfillmentDetailInput.strategy});
            trackMicroConversion(MicroConversionEnum.updateOrderType);
          }
          if (fulfillmentDetailInput?.corpAccountId) {
            trackEvent('order-account-updated', {
              corpAccountId: values?.orderAccount?.id,
              isParent: values?.orderAccount?.isParent,
            });
            trackMicroConversion(MicroConversionEnum.updateOrderCorpAccount);
          }

          if (fulfillmentDetailInput.eventLocalTime || fulfillmentDetailInput.eventOn) {
            trackEvent('date-time-updated', {
              eventDate: values?.eventDate,
              eventTime: values?.eventTime,
            });
            trackMicroConversion(MicroConversionEnum.updateEventDateTime);
          }
        }

        // address is valid and on location dropdown
        if (isLocationOpen && addressIsValid) {
          formikHelpers.resetForm();
          onDropdownDismiss(
            {updated: !!data, fulfillmentDetailId: data?.fulfillmentDetail?.id},
            trackingParams,
          );
        } else {
          formikHelpers.resetForm();
          onDropdownDismiss(
            {updated: true, fulfillmentDetailId: data?.fulfillmentDetail?.id},
            trackingParams,
          );
        }
      }}
    >
      {({values, handleReset, setFieldValue}) => {
        return (
          <ClickAwayListener
            onClickAway={e => {
              // The confirmation modal renders in a DOM portal, so clicks inside it appear
              // "outside" the EventBar to react-click-away-listener. Guard here so the modal
              // can handle its own dismissal without dismissActiveDropdown re-opening it.
              if (isApplyDetailsConfirmationOpen) return;
              // The Radix Select portal unmounts its content before the click-away fires,
              // so data-eventbar-ignore on Select.Content is already detached from DOM.
              // Track open state directly to prevent the popup from closing on item selection.
              if (isLocationSelectOpen) return;
              dismissActiveDropdown({
                trackingMisc: undefined,
                event: e as Event,
                ...(fulfillmentDetailState !== FulfillmentDetailStates.NONE && {
                  dirty: dirty(values),
                }),
              });
            }}
          >
            <div className="flex h-12 w-[512px] items-center rounded-lg">
              <EventBarPopup
                className="rounded-l-full rounded-r-none"
                clickHandler={() => {
                  // edge case where someone clears address input and navigates away from location dropdown
                  if (values.addressString === '') {
                    setFieldValue('addressString', initialValues.addressString);
                  }
                  dismissActiveDropdown({});
                  handleDismissCoachmark();
                  if (!isLocationOpen) {
                    fulfillmentDetailClickHandler(FulfillmentDetailStates.LOCATION);
                    trackEvent('location-details-clicked');
                  }
                }}
                isOpen={isLocationOpen}
                icon={faLocationDot}
                label={
                  values?.addressString ||
                  fallbackAddressString ||
                  t('eventBar.eventLocation.label')
                }
                data-testid="location-dropdown"
              >
                <Location
                  multipleAddresses={multipleAddresses}
                  onSelectOpenChange={setIsLocationSelectOpen}
                  setMultipleAddresses={setMultipleAddresses}
                  setShowIncompleteAddressWarning={setShowIncompleteAddressWarning}
                  showIncompleteAddressWarning={showIncompleteAddressWarning}
                  trackEvent={trackEvent}
                  validateAddress={validateAddress}
                />
              </EventBarPopup>

              {isEventBarCoachmarkVisible && (
                <Coachmark
                  clickHandler={() => {
                    fulfillmentDetailClickHandler(FulfillmentDetailStates.NONE);
                    handleDismissCoachmark();
                    tracker.trackClick('event-bar-coachmark-dismiss-button');
                  }}
                  isOpen={isCoachmarkOpen}
                ></Coachmark>
              )}

              <ApplyDetailsConfirmationModal
                isOpen={isApplyDetailsConfirmationOpen}
                onReset={() => {
                  handleReset();
                  clearAddressWarnings();
                }}
              />

              <EventBarPopup
                className="rounded-none border-x-0"
                clickHandler={() => {
                  // edge case where someone clears address input and navigates away from location dropdown
                  if (values.addressString === '') {
                    setFieldValue('addressString', initialValues.addressString);
                  }
                  dismissActiveDropdown({});
                  handleDismissCoachmark();
                  if (!isDateTimeOpen) {
                    fulfillmentDetailClickHandler(FulfillmentDetailStates.DATETIME);
                    trackEvent('date-time-clicked');
                  }
                }}
                icon={faCalendar}
                isOpen={isDateTimeOpen}
                label={
                  eventAt
                    ? formatEventBarDate(eventAt, address?.timeZoneIdentifier)
                    : values.eventDate
                      ? format(parseISO(values.eventDate), 'EEE, M/d')
                      : t('eventBar.eventDate.label')
                }
                popupClasses="w-auto min-w-0"
              >
                <DateTime trackEvent={trackEvent} />
              </EventBarPopup>

              <EventBarPopup
                className="rounded-l-none rounded-r-full"
                clickHandler={() => {
                  // edge case where someone clears address input and navigates away from location dropdown
                  if (values.addressString === '') {
                    setFieldValue('addressString', initialValues.addressString);
                  }
                  dismissActiveDropdown({});
                  handleDismissCoachmark();
                  if (!isDetailsOpen) {
                    trackEvent('event-details-clicked');
                    fulfillmentDetailClickHandler(FulfillmentDetailStates.DETAILS);
                  }
                }}
                isOpen={isDetailsOpen}
                icon={faUsers}
                label={t('eventBar.eventDetails.label')}
              >
                <Details trackEvent={trackEvent} />
              </EventBarPopup>
            </div>
          </ClickAwayListener>
        );
      }}
    </Formik>
  );
};

export default EventBar;
